/**
 * ============================================================
 * useStore.ts — Ponto de entrada principal do Zustand Store
 * ============================================================
 *
 * Responsabilidade: Compor todos os slices em uma unica store global
 *   da aplicacao, usando o padrao "sliced store" do Zustand.
 * Arquitetura: Cada dominio (UI, deals, kanban, colunas, roles,
 *   workspace) vive em seu proprio slice. Este arquivo os combina
 *   via intersecao de tipos (AppState = A & B & C ...) e spread
 *   dos StateCreators.
 * Padroes: Zustand Slices Pattern, Type-safe StateCreator casts
 * Dependencias:
 *   - Depende de: todos os slices em ./slices/*, ./types
 *   - Quem depende dele: praticamente toda a aplicacao (componentes,
 *     hooks, services) importam useStore ou re-exports daqui.
 *
 * Observacao: Os casts "as StateCreator<AppState, [], [], XSlice>"
 *   sao necessarios porque cada slice e tipado isoladamente, mas
 *   o Zustand precisa do tipo composto para a store final.
 * ============================================================
 */

import { create } from 'zustand';
import type { StateCreator } from 'zustand';

// ── Tipos (fonte canonica) ──
// Re-exporta todos os tipos do arquivo centralizado para que consumidores
// possam importar diretamente de useStore sem conhecer ./types
export type {
  Deal, KanbanColumnDef, DashboardCardConfig, Workflow,
  ContactLog, CustomFieldDef, TeamMember, Workspace, Project,
  ProjectType, UserProfile, DatePreset, DateFilterState, KanbanColumn,
  Priority, ClientCategory, ServiceType, LeadSource, ProposalAttachment,
  StatusHistoryEntry, CompanyInfo, ContactInfo, ProjectMember, LeadsColumnConfig,
  CustomRoleGroup, PermissionKey,
} from './types';

// Re-exporta funcoes de scoring para consumidores que importam de useStore
export { calculateScore, calculateScoreSimilarity } from '@/lib/scoring';

// ── Importacao dos Slices ──
// Cada slice exporta sua factory (createXSlice) e seu tipo (XSlice)
import { createUISlice, type UISlice } from './slices/uiSlice';
import { createDealsSlice, type DealsSlice } from './slices/dealsSlice';
import { createKanbanSlice, type KanbanSlice } from './slices/kanbanSlice';
import { createColumnsSlice, type ColumnsSlice } from './slices/columnsSlice';
import { createRolesSlice, type RolesSlice } from './slices/rolesSlice';
import { createWorkspaceSlice, type WorkspaceSlice } from './slices/workspaceSlice';

// Re-exporta valores default para consumidores (ex: reset de colunas, roles iniciais)
export { DEFAULT_LEADS_COLUMNS, DEFAULT_CLIENTS_COLUMNS } from './slices/columnsSlice';
export { DEFAULT_CUSTOM_ROLE_GROUPS } from './slices/rolesSlice';

// ── Tipo Composto da Store ──
// Intersecao de todos os slices forma o estado global completo
export type AppState =
  & UISlice
  & DealsSlice
  & KanbanSlice
  & ColumnsSlice
  & RolesSlice
  & WorkspaceSlice;

// ── Criacao da Store Global ──
// Cada slice e instanciado com os mesmos args (set, get, api) do Zustand.
// O spread combina todos os estados e acoes em um unico objeto.
// A ordem dos slices nao importa pois nao ha sobreposicao de chaves.
export const useStore = create<AppState>()((...args) => ({
  ...(createUISlice as StateCreator<AppState, [], [], UISlice>)(...args),
  ...(createDealsSlice as StateCreator<AppState, [], [], DealsSlice>)(...args),
  ...(createKanbanSlice as StateCreator<AppState, [], [], KanbanSlice>)(...args),
  ...(createColumnsSlice as StateCreator<AppState, [], [], ColumnsSlice>)(...args),
  ...(createRolesSlice as StateCreator<AppState, [], [], RolesSlice>)(...args),
  ...(createWorkspaceSlice as StateCreator<AppState, [], [], WorkspaceSlice>)(...args),
}));
